<template>
  <el-dialog
    :model-value="modelValue"
    :title="dialogTitle"
    width="min(1100px, 96vw)"
    top="4vh"
    class="erp-nonmodal-work-dialog"
    :modal="false"
    :modal-penetrable="true"
    :lock-scroll="false"
    destroy-on-close
    :close-on-click-modal="false"
    @update:model-value="emit('update:modelValue', $event)"
    @closed="onClosed"
    @opened="onOpened"
  >
    <div v-loading="loading">
      <template v-if="order">
        <el-alert
          v-if="packagingOperatorName"
          type="info"
          :closable="false"
          show-icon
          class="pack-alert"
          :title="`包装填写已锁定：${packagingOperatorName}`"
        >
          <template #default>
            保存后自动锁定填写人；业务可在派工单详情解除锁定。
            <template v-if="order.packaging_operator_assigned_at">
              · 锁定时间 {{ formatDt(order.packaging_operator_assigned_at) }}
            </template>
          </template>
        </el-alert>
        <p v-if="order.packaging_last_saved_by_name" class="pack-meta muted">
          最近保存：{{ order.packaging_last_saved_by_name }}
          <template v-if="order.packaging_last_saved_at"> · {{ formatDt(order.packaging_last_saved_at) }}</template>
        </p>

        <el-alert
          v-if="pendingAccess && canReviewAccess"
          type="warning"
          :closable="false"
          show-icon
          class="pack-alert"
          title="有待处理的旧版包装填写申请（可忽略或驳回）"
        >
          <template #default>
            <p>
              申请人 <strong>{{ pendingAccess.requester_name || '—' }}</strong>
              · {{ formatDt(pendingAccess.requested_at) }}
              <span v-if="pendingAccess.reason"> · {{ pendingAccess.reason }}</span>
            </p>
            <div class="pack-alert__actions">
              <el-button type="success" size="small" :loading="accessAction === 'approve'" @click="approvePending">
                同意指定
              </el-button>
              <el-button type="danger" size="small" plain :loading="accessAction === 'reject'" @click="rejectPending">
                驳回
              </el-button>
            </div>
          </template>
        </el-alert>

        <el-descriptions :column="3" border size="small" class="pack-summary">
          <el-descriptions-item label="袋型(整单回退)">{{ packagingTypeLabel(order.packaging_type) }}</el-descriptions-item>
          <el-descriptions-item label="需称重">{{ needWeighAny ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="需量体积">{{ needVolumeAny ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="包装核销" :span="3">{{ packagingAuditLabel }}</el-descriptions-item>
        </el-descriptions>

        <el-alert
          v-if="!packLines.length && !loading"
          type="warning"
          :closable="false"
          show-icon
          class="pack-alert"
          title="暂无分码数据"
          description="请先在移动端「跑码」保存分码后，再填写各支称重与外尺寸。"
        />

        <el-table v-else :data="packLines" border stripe max-height="52vh" class="pack-table">
          <el-table-column prop="branch_pack_code" label="包装编号" min-width="150" fixed="left" />
          <el-table-column label="标签" width="72" align="center">
            <template #default="{ row }">{{ row.displayLabel || row.branch_label || '—' }}</template>
          </el-table-column>
          <el-table-column prop="order_no" label="订单号" width="120" show-overflow-tooltip />
          <el-table-column label="客户" min-width="100" show-overflow-tooltip>
            <template #default="{ row }">{{ row.customerName || '—' }}</template>
          </el-table-column>
          <el-table-column label="内部材料" min-width="120" show-overflow-tooltip>
            <template #default="{ row }">{{ row.internalMaterialName || '—' }}</template>
          </el-table-column>
          <el-table-column label="内部颜色" min-width="140" show-overflow-tooltip>
            <template #default="{ row }">{{ row.internalColorLine || '—' }}</template>
          </el-table-column>
          <el-table-column label="数量" width="88" align="right">
            <template #default="{ row }">{{ fmtNumMaybe1(row.quantity) }} {{ row.unit }}</template>
          </el-table-column>
          <el-table-column label="称重" width="72" align="center">
            <template #default="{ row }">{{ row.needWeigh ? '要' : '—' }}</template>
          </el-table-column>
          <el-table-column label="体积" width="72" align="center">
            <template #default="{ row }">{{ row.needVolume ? '要' : '—' }}</template>
          </el-table-column>
          <el-table-column label="重量 kg" width="120">
            <template #default="{ row }">
              <el-input
                v-if="canEditMeasures && row.needWeigh"
                v-model="row.weightStr"
                size="small"
                placeholder="如 12.5"
                clearable
              />
              <span v-else>{{ row.needWeigh ? row.weightStr || '—' : '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="长 cm" width="88">
            <template #default="{ row }">
              <el-input
                v-if="canEditMeasures && row.needVolume"
                v-model="row.lenStr"
                size="small"
                placeholder="整数"
                clearable
              />
              <span v-else>{{ row.needVolume ? row.lenStr || '—' : '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="宽 cm" width="88">
            <template #default="{ row }">
              <el-input
                v-if="canEditMeasures && row.needVolume"
                v-model="row.widStr"
                size="small"
                placeholder="整数"
                clearable
              />
              <span v-else>{{ row.needVolume ? row.widStr || '—' : '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="高 cm" width="88">
            <template #default="{ row }">
              <el-input
                v-if="canEditMeasures && row.needVolume"
                v-model="row.hgtStr"
                size="small"
                placeholder="整数"
                clearable
              />
              <span v-else>{{ row.needVolume ? row.hgtStr || '—' : '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="录入人" min-width="140" show-overflow-tooltip>
            <template #default="{ row }">
              <template v-if="row.packagingMeasuredByName">
                {{ row.packagingMeasuredByName }}
                <span v-if="row.packagingMeasuredAt" class="muted"> · {{ formatDt(row.packagingMeasuredAt) }}</span>
              </template>
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>
        </el-table>

        <el-alert
          v-if="readonlyTip && !canEditMeasures"
          type="info"
          :closable="false"
          show-icon
          class="pack-alert pack-readonly"
          :title="readonlyTip"
        />
      </template>
    </div>

    <template #footer>
      <el-button @click="emit('update:modelValue', false)">关闭</el-button>
      <el-button
        v-if="canSubmitAudit && (needWeighAny || needVolumeAny) && packLines.length"
        type="warning"
        plain
        :loading="submittingAudit"
        @click="submitPackagingAudit"
      >
        提交包装核销
      </el-button>
      <el-button
        v-if="canEditMeasures && (needWeighAny || needVolumeAny) && packLines.length"
        type="primary"
        :loading="saving"
        :disabled="!canSavePackaging"
        @click="saveAll"
      >
        保存全部支数实测
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getDispatchOrder, saveDispatchBranchPackMeasures, submitPackagingAuditDispatchOrder } from '@/api/dispatchOrders'
import {
  approveDispatchPackagingAccessRequest,
  rejectDispatchPackagingAccessRequest
} from '@/api/dispatchPackagingAccess'
import { useUserStore } from '@/store/user'
import {
  canOperateDispatchPackaging,
  canEditDispatchPackagingMeasures,
  canSubmitPackagingAudit,
  packagingAuditStatusLabel,
  packagingOperatorDisplay
} from '@/utils/dispatchMobilePermissions'
import { hasPermission } from '@/utils/permission'
import {
  buildPackLinesFromDispatchOrder,
  fmtNumMaybe1,
  packagingTypeLabel,
  parsePackDimCm,
  parsePackWeightKg
} from '@/utils/dispatchPackagingMeasures'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  dispatchOrderId: { type: [Number, String], default: null }
})

const emit = defineEmits(['update:modelValue', 'saved', 'access-changed'])

const userStore = useUserStore()
const loading = ref(false)
const saving = ref(false)
const submittingAudit = ref(false)
const order = ref(null)
const packLines = ref([])
const accessAction = ref('')

const canReviewAccess = computed(() => {
  const u = userStore.user
  if (!u) return false
  if (u.is_admin || u.is_superuser) return true
  return (
    hasPermission(u, 'orders.dispatch.edit') ||
    hasPermission(u, 'orders.dispatch.review')
  )
})

const dialogTitle = computed(() => {
  const no = order.value?.dispatch_order_no
  return no ? `包装实测 · ${no}` : '包装实测'
})

const packagingOperatorName = computed(() => packagingOperatorDisplay(order.value))

const pendingAccess = computed(() => {
  const p = order.value?.packaging_access_request_pending
  if (!p || p.status !== 'pending') return null
  return p
})

const canOperatePackaging = computed(() => canOperateDispatchPackaging(userStore.user, order.value))

const canEditMeasures = computed(() =>
  canEditDispatchPackagingMeasures(userStore.user, order.value, packLines.value, {
    packagingMeasuresIncomplete: !!order.value?.packaging_measures_incomplete
  })
)

const canSavePackaging = computed(() => canEditMeasures.value)

const canSubmitAudit = computed(() => canSubmitPackagingAudit(order.value, packLines.value))

const packagingAuditLabel = computed(() =>
  packagingAuditStatusLabel(order.value?.packaging_audit_status)
)

const items = computed(() => (Array.isArray(order.value?.items) ? order.value.items : []))

const needWeighAny = computed(() => {
  const o = order.value
  if (!o) return false
  if (o.packaging_require_weigh) return true
  return items.value.some((it) => it.packaging_require_weigh)
})

const needVolumeAny = computed(() => {
  const o = order.value
  if (!o) return false
  if (o.packaging_require_volume) return true
  return items.value.some((it) => it.packaging_require_volume)
})

const readonlyTip = computed(() => {
  if (!order.value) return ''
  if (order.value.status === 'cancelled') return '派工单已取消，仅供查看。'
  const pa = (order.value.packaging_audit_status || 'na').toLowerCase()
  if (pa === 'pending') return '包装资料已提交业务核销，待审核期间不能修改。'
  if (pa === 'approved') return '包装核销已通过，包装实测已锁定。'
  if (pa === 'rejected') return '包装核销已退回，请修改后重新提交包装核销。'
  const op = packagingOperatorName.value
  if (op && !canOperatePackaging.value) {
    return `本单包装资料已由「${op}」保存锁定，您无权修改；业务可在派工单详情解除锁定。`
  }
  return ''
})

function formatDt(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16).replace('T', ' ')
  return d.toLocaleString('zh-CN', { hour12: false })
}

async function load() {
  const id = Number(props.dispatchOrderId)
  if (!Number.isFinite(id) || id <= 0) return
  loading.value = true
  try {
    const res = await getDispatchOrder(id, {
      params: { packaging_context: 'true' }
    })
    if (res.success && res.data) {
      order.value = res.data
      packLines.value = buildPackLinesFromDispatchOrder(res.data)
    } else {
      ElMessage.error(res.message || '加载失败')
      order.value = null
      packLines.value = []
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载失败')
    order.value = null
    packLines.value = []
  } finally {
    loading.value = false
  }
}

async function saveAll() {
  if (!canSavePackaging.value) {
    ElMessage.warning(readonlyTip.value || '无权保存')
    return
  }
  const id = Number(props.dispatchOrderId)
  const measures = []
  const missingId = []
  for (const row of packLines.value) {
    if (!row.needWeigh && !row.needVolume) continue
    if (!row.branch_record_id) {
      missingId.push(row.branch_pack_code || row.displayLabel || '?')
      continue
    }
    const m = { branch_record_id: row.branch_record_id }
    if (row.needWeigh) m.weight_kg = parsePackWeightKg(row.weightStr)
    if (row.needVolume) {
      m.length_cm = parsePackDimCm(row.lenStr)
      m.width_cm = parsePackDimCm(row.widStr)
      m.height_cm = parsePackDimCm(row.hgtStr)
    }
    measures.push(m)
  }
  if (missingId.length) {
    ElMessage.warning('部分分码尚未保存跑码，请先在移动端保存分码后再填写称重')
    return
  }
  if (!measures.length) {
    ElMessage.info('本单未要求称重或量体积，无需保存')
    return
  }
  saving.value = true
  try {
    const res = await saveDispatchBranchPackMeasures(id, { measures })
    if (res.success) {
      ElMessage.success(res.message || '已保存')
      await load()
      emit('saved')
    } else {
      ElMessage.error(res.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    saving.value = false
  }
}

async function submitPackagingAudit() {
  if (!canSubmitAudit.value) {
    ElMessage.warning('请先保存并填齐全部支数称重/外尺寸后再提交')
    return
  }
  const id = Number(props.dispatchOrderId)
  try {
    await ElMessageBox.confirm(
      '提交后包装资料将进入业务核销，提交期间及通过后不能再修改。确认提交？',
      '提交包装核销',
      { type: 'warning', confirmButtonText: '提交', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  submittingAudit.value = true
  try {
    const res = await submitPackagingAuditDispatchOrder(id, {})
    if (res.success) {
      ElMessage.success(res.message || '已提交包装核销')
      await load()
      emit('saved')
    } else {
      ElMessage.error(res.message || '提交失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '提交失败')
  } finally {
    submittingAudit.value = false
  }
}

async function approvePending() {
  const p = pendingAccess.value
  if (!p?.access_request_id) return
  try {
    await ElMessageBox.confirm(
      `确认指定「${p.requester_name || '该同事'}」负责本单包装资料填写？`,
      '同意包装填写申请',
      { type: 'warning', confirmButtonText: '同意指定', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  accessAction.value = 'approve'
  try {
    const res = await approveDispatchPackagingAccessRequest(p.access_request_id, {})
    if (res.success) {
      ElMessage.success(res.message || '已指定')
      await load()
      emit('access-changed')
      emit('saved')
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '操作失败')
  } finally {
    accessAction.value = ''
  }
}

async function rejectPending() {
  const p = pendingAccess.value
  if (!p?.access_request_id) return
  let note = ''
  try {
    const { value } = await ElMessageBox.prompt('请输入驳回说明（选填）', '驳回包装填写申请', {
      confirmButtonText: '驳回',
      cancelButtonText: '取消',
      inputPlaceholder: '选填'
    })
    note = (value || '').trim()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(String(e?.message || e))
    return
  }
  accessAction.value = 'reject'
  try {
    const res = await rejectDispatchPackagingAccessRequest(p.access_request_id, { review_notes: note })
    if (res.success) {
      ElMessage.success(res.message || '已驳回')
      await load()
      emit('access-changed')
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '操作失败')
  } finally {
    accessAction.value = ''
  }
}

function onOpened() {
  load()
}

function onClosed() {
  order.value = null
  packLines.value = []
}

watch(
  () => props.dispatchOrderId,
  () => {
    if (props.modelValue) load()
  }
)
</script>

<style scoped>
.pack-alert {
  margin-bottom: 12px;
}
.pack-alert__actions {
  margin-top: 8px;
}
.pack-meta {
  margin: 0 0 12px;
  font-size: 13px;
}
.pack-summary {
  margin-bottom: 12px;
}
.pack-table {
  width: 100%;
}
.pack-readonly {
  margin-top: 12px;
}
.pack-mt {
  margin-top: 8px;
}
.pack-dialog-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
.muted {
  color: var(--el-text-color-secondary);
}
</style>
